import {
  prepareZXingModule as prepareReaderModule,
  readBarcodesFromImageData,
} from "zxing-wasm/reader";
import {
  prepareZXingModule as prepareWriterModule,
  writeBarcode,
} from "zxing-wasm/writer";
import readerWasm from "zxing-wasm/reader/zxing_reader.wasm?url";
import writerWasm from "zxing-wasm/writer/zxing_writer.wasm?url";

/**
 * Where the two WebAssembly binaries come from.
 *
 * Left alone, zxing-wasm resolves its own `.wasm` from `fastly.jsdelivr.net`
 * on first use. A verifier standing in a corridor with no network would then
 * never decode anything, which is the one thing this application exists to do.
 * The `?url` imports make Vite emit each binary as an ordinary bundled asset
 * and hand back its same-origin path, so the service worker can precache them
 * like any other asset and `locateFile` below never names a host.
 *
 * They are exported because no unit test can prove the absence of a request.
 * `tests/offline.test.ts` pins the reachable property instead: these two
 * strings carry no scheme and no host.
 */
export const READER_WASM_URL: string = readerWasm;
export const WRITER_WASM_URL: string = writerWasm;

prepareReaderModule({ overrides: { locateFile: () => READER_WASM_URL } });
prepareWriterModule({ overrides: { locateFile: () => WRITER_WASM_URL } });

/**
 * Returns one SVG string per frame.
 *
 * VS1 always returns a single frame. The list is the shape the animated
 * fallback needs, so callers never change when it arrives: if a v20 symbol
 * turns out not to scan screen to screen, this splits into "part k of N" at
 * version 15 and every caller keeps working.
 */
export async function emitQR(payload: Uint8Array): Promise<string[]> {
  const written = await writeBarcode(payload, {
    format: "QRCode",
    ecLevel: "L",
    withQuietZones: true,
  });
  if (written.error) throw new Error(`QR generation failed: ${written.error}`);
  return [withViewBox(written.svg)];
}

/**
 * The writer emits width and height in module units and no viewBox, so a CSS
 * width would not scale the drawing. Swapping them for a viewBox makes the
 * symbol responsive, and crispEdges keeps the modules square when it scales
 * up, which is exactly what a camera needs.
 */
function withViewBox(svg: string): string {
  const match = /<svg width="(\d+)" height="(\d+)"/.exec(svg);
  if (!match) throw new Error("unexpected SVG from the QR writer");
  return svg.replace(
    match[0],
    `<svg viewBox="0 0 ${match[1]} ${match[2]}" shape-rendering="crispEdges"`,
  );
}

export function qrModuleCount(svg: string): number {
  return Number(/viewBox="0 0 (\d+)/.exec(svg)?.[1] ?? NaN);
}

export async function decodeQRBytes(image: ImageData): Promise<Uint8Array | null> {
  const results = await readBarcodesFromImageData(image, {
    formats: ["QRCode"],
    tryHarder: true,
  });
  const bytes = results[0]?.bytes;
  return bytes ? new Uint8Array(bytes) : null;
}
